import { database } from '@app/firebase';
import AppBackgroundColor from '@components/app-background-color/AppBackgroundColor';
import ShoppableRooms from '@components/shoppable-home/ShoppableRooms';
import useCollection from '@hooks/useCollection';
import { ContentBlock } from '@layouts/ContentBlocks';
import { highestOffer } from '@ui/components/product-offer/ProductOffer';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import { hasDropshippableOffer } from '@ui/utils/hasDropshippableOffer';
import homeMetaData from '@ui/utils/metadata/homeMetaData';
import { alphabeticRoomCompare, roomCompare } from '@ui/utils/sortRooms';
import properCase from '@utils/properCase';
import { makeSeoData } from 'config/next-seo.config';
import { collection, collectionGroup, doc, getDoc, getDocs, query as firestoreQuery, where } from 'firebase/firestore';
import { cloneDeep } from 'lodash';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { NextSeo } from 'next-seo';
import React, { useMemo } from 'react';

import { DefaultFirestoreConverter } from '../../../../../types/DefaultFirestoreConverter';
import useStyles from './home.style';

export type RoomRequests = {
  roomID: guesthouse.ProductRequest[];
}

export type RoomLite = {
  title: string;
  productCount: number;
  cartRoomProduct?: guesthouse.CartRoomProduct;
  roomDocID?: string;
  docID?: string;
}

export interface RoomShoppable extends RoomLite {
  subRooms?: RoomLite[];
}

type PseudoRoomsAndProductCounts = {
  roomsAllProducts: RoomShoppable[],
  roomsAllProductsCount: number,
  roomsDropshipProducts: RoomShoppable[],
  roomsDropshipProductsCount: number,
}

const EMPTY_ENTIRE_HOME_PSEUDO_ROOM: RoomShoppable = {
  title: 'Entire home',
  productCount: 0,
  subRooms: [],
};

type CalcRoomProductCountsRet = {
  roomAllProductsCount: number,
  roomDropshipProductsCount: number
}

const calculateRoomProductCounts = (roomProductRequests: guesthouse.ProductRequest[] = []): CalcRoomProductCountsRet => {
  return roomProductRequests
    .reduce((
      acc: CalcRoomProductCountsRet,
      curr: guesthouse.ProductRequest
    ): CalcRoomProductCountsRet => {
      if (curr.inventory?.offer || curr.product?.offers?.length) {
        acc.roomAllProductsCount += 1;

        if (hasDropshippableOffer(curr.product?.offers)) {
          acc.roomDropshipProductsCount += 1;
        }
      }

      return acc;
    }, {
      roomAllProductsCount: 0,
      roomDropshipProductsCount: 0,
    });
};

const buildPseudoRoom = (room: guesthouse.Room, roomAllProductsCount: number, roomTypeIncrementer: Record<string, number>, productRequests: guesthouse.ProductRequest[], type: 'all-products' | 'dropship-products'): RoomShoppable => {
  const roomTypeProper = room.type ? properCase(room.type?.replace('-', ' ')) : room.title;

  if (typeof roomTypeIncrementer?.[roomTypeProper] === 'number') {
    roomTypeIncrementer[roomTypeProper] += 1;
  } else {
    roomTypeIncrementer[roomTypeProper] = 0;
  }
  const roomTitle = roomTypeIncrementer[roomTypeProper] > 0 ? `${roomTypeProper} ${roomTypeIncrementer[roomTypeProper]}` : roomTypeProper;
  const pseudoRoom: RoomShoppable = {
    docID: room.docID,
    title: roomTitle,
    productCount: roomAllProductsCount,
    roomDocID: room.docID,
    cartRoomProduct: buildCartRoomProduct(productRequests, room, type)
  };

  return pseudoRoom;
};

const buildEntireHomePseudoRoom = (rooms: RoomShoppable[]): RoomShoppable => {
  const entireHome: RoomShoppable = rooms.reduce((entireHomeAccumulator: RoomShoppable, pseudoRoom: RoomShoppable) => {
    entireHomeAccumulator.productCount += pseudoRoom.productCount;
    entireHomeAccumulator.subRooms.push(pseudoRoom);

    return entireHomeAccumulator;
  }, cloneDeep(EMPTY_ENTIRE_HOME_PSEUDO_ROOM));


  return entireHome;
};

type BuildPseudoRoomsRet = {
  pseudoRooms: RoomShoppable[],
  productCount: number,
};

const buildPseudoRooms = (rooms: guesthouse.Room[], roomProductRequests: RoomRequests, type: 'all-products' | 'dropship-products'): BuildPseudoRoomsRet => {
  const pseudoRooms: RoomShoppable[] = [];
  let productCount = 0;
  const roomTypeIncrementer: Record<string, number> = {};

  const isHomeBuyerType = type === 'all-products';

  for (const roomDocID in roomProductRequests) {
    const room = rooms.find(room => room.docID === roomDocID);

    const { roomAllProductsCount, roomDropshipProductsCount } = calculateRoomProductCounts(roomProductRequests[roomDocID]);

    const roomProductCount: number = isHomeBuyerType ? roomAllProductsCount : roomDropshipProductsCount;

    productCount += roomProductCount;
    const pseudoRoom: RoomShoppable = buildPseudoRoom(room, roomProductCount, roomTypeIncrementer, roomProductRequests[roomDocID], type);

    pseudoRooms.push(pseudoRoom);
  }

  return {
    pseudoRooms,
    productCount
  };
};

const buildPseudoRoomsAndProductCounts = (rooms: guesthouse.Room[], roomProductRequests: RoomRequests): PseudoRoomsAndProductCounts => {
  const { pseudoRooms: roomsAllProducts, productCount: roomsAllProductsCount } = buildPseudoRooms(rooms, roomProductRequests, 'all-products');
  const { pseudoRooms: roomsDropshipProducts, productCount: roomsDropshipProductsCount } = buildPseudoRooms(rooms, roomProductRequests, 'dropship-products');

  roomsAllProducts.unshift(buildEntireHomePseudoRoom(roomsAllProducts));
  roomsDropshipProducts.unshift(buildEntireHomePseudoRoom(roomsDropshipProducts));

  return {
    roomsAllProducts,
    roomsAllProductsCount,
    roomsDropshipProducts,
    roomsDropshipProductsCount,
  };
};

const convertProductRequestToCartProduct = (productRequest: guesthouse.ProductRequest, type: 'all-products' | 'dropship-products', room?: guesthouse.Room): guesthouse.CartProduct => {
  if (type === 'all-products' || (type === 'dropship-products' && hasDropshippableOffer(productRequest.product?.offers))) {

    const cartProduct: guesthouse.CartProduct = {
      quantity: productRequest.quantity,
      product: productRequest.product,
      offer: productRequest?.inventory?.offer || highestOffer(productRequest.product.offers),
      room: room || productRequest.room,
    };

    if (type === 'dropship-products') {
      const inventoryDropshipOffer = productRequest?.inventory?.offer?.classification === 'dropship' ? productRequest.inventory.offer : undefined;
      const dropshipOffers = productRequest.product?.offers?.filter((offer: guesthouse.ProductOffer) => offer.classification === 'dropship');

      cartProduct.offer = inventoryDropshipOffer ? inventoryDropshipOffer : dropshipOffers.length > 0 ? highestOffer(dropshipOffers) : highestOffer(productRequest.product.offers);
    }

    return cartProduct;
  }
};

const convertProductRequestsToCartProducts = (productRequests: guesthouse.ProductRequest[], type: 'all-products' | 'dropship-products', room?: guesthouse.Room): guesthouse.CartProduct[] => {
  return productRequests.map((productRequest) => convertProductRequestToCartProduct(productRequest, type, room)).filter(productRequest => !!productRequest);
};

const buildCartRoomProduct = (productRequests: guesthouse.ProductRequest[], room: guesthouse.Room, type: 'all-products' | 'dropship-products'): guesthouse.CartRoomProduct => {
  return {
    room,
    cartProducts: convertProductRequestsToCartProducts(productRequests, type, room),
  };
};

const generateRoomProductRequests = (requests: guesthouse.ProductRequest[], rooms: guesthouse.Room[]): RoomRequests => {
  const roomRequestsObj = {} as RoomRequests;

  rooms.forEach(room => {
    roomRequestsObj[room.docID] = requests
      .filter(r => r.room.docID === room?.docID);
  });

  return roomRequestsObj;
};

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  let homeData: guesthouse.Home | null = null;

  let queryHomeId: string;

  if (typeof query.home === 'object') {
    queryHomeId = query.home[0];
  } else {
    queryHomeId = query.home;
  }

  const home = await getDoc<guesthouse.Home>(doc(database, `homes/${queryHomeId}`).withConverter(new DefaultFirestoreConverter<guesthouse.Home>()));

  homeData = home.data();

  if (!homeData?.docID) {
    const homeDocs = await getDocs(firestoreQuery(collection(database, 'homes').withConverter(new DefaultFirestoreConverter<guesthouse.Home>()),
      where('slug', '==', query.home)));

    homeData = homeDocs?.docs?.length && homeDocs.docs[0].data();
  }

  if (!homeData?.docID) {
    return {
      notFound: true,
    };
  }

  let roomsData = [];

  if (homeData.docID) {
    const homeDoc = doc(collection(database, 'homes'), homeData.docID);
    const homeRoomsCollection = collection(homeDoc, 'rooms').withConverter(new DefaultFirestoreConverter<guesthouse.Room>());
    const rooms = await getDocs<guesthouse.Room>(firestoreQuery(homeRoomsCollection, where('published', '==', true)));

    roomsData = rooms?.docs.map(r => r.data());
  }

  roomsData.sort(alphabeticRoomCompare).sort(roomCompare);

  const props = {
    home: JSON.stringify(homeData),
    rooms: JSON.stringify(roomsData),
    metadata: JSON.stringify(homeMetaData(homeData)),
  };

  return { props };
};


function HomePage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { theme, classes } = useStyles();
  const { md } = useBreakpoints();

  const home = JSON.parse(props.home) as guesthouse.Home;
  const rooms = JSON.parse(props.rooms) as guesthouse.Room[];
  const metadata = JSON.parse(props.metadata) as guesthouse.MetaData;
  const { collection: requestDocs, loading } = useCollection(
    firestoreQuery(collectionGroup(database, 'requests')
      ,where('home.docID', '==', home.docID)
      ,where('controller', '==', true))
  );

  const roomProductRequests = useMemo(() => {
    if (requestDocs?.docs.length) {
      return generateRoomProductRequests(requestDocs.docs.map(req => req.data() as guesthouse.ProductRequest), rooms);
    }
  }, [requestDocs?.docs]);

  const {
    roomsAllProducts
    // roomsDropshipProducts,
  } = useMemo<PseudoRoomsAndProductCounts>(() => buildPseudoRoomsAndProductCounts(rooms, roomProductRequests), [rooms, JSON.stringify(roomProductRequests)]);

  return (
    <>
      <AppBackgroundColor
        backgroundColor={theme.gh_vars.defaultBackgroundColor}
        borderColor={theme.gh_vars.defaultBackgroundColor}
        headerColor={theme.gh_vars.defaultBackgroundColor}
      />
      <Head>
        <title>
          {metadata.pageTitle}
        </title>

        {!home.published && (
          <meta
            name="robots"
            content="noindex"
          />
        )}
      </Head>

      <NextSeo {...makeSeoData(metadata)} />

      <ContentBlock
        margin={md ? [10, 'auto'] : [10, 0, 'auto']}
        className={classes.contentBlock}
        padding={md ? theme.spacing(0, 3) : 0}
      >
        <ShoppableRooms
          home={home}
          pseudoRooms={roomsAllProducts}
          loading={loading}
        />
      </ContentBlock>
    </>
  );
}

export default HomePage;
